import React, { useEffect, useMemo, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import { getRoles, getUsers } from "../../../Auth";
import { STAFF_PAY_VISIT_NOTES } from "../../../services/librariesCatalogService";

const EMPTY = {
  visitNote: "SOC",
  roleName: "",
  userId: "",
  rate: "",
  code: "",
  status: "Active",
};

const StaffPayRateFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            visitNote: record.visitNote || "SOC",
            roleName: record.roleName || "",
            userId: record.userId ? String(record.userId) : "",
            rate:
              record.rate === undefined || record.rate === null
                ? ""
                : String(record.rate),
            code: record.code || "",
            status: record.status || "Active",
          }
        : EMPTY,
    );
    getRoles().then((list) => setRoles(Array.isArray(list) ? list : []));
  }, [isOpen, record]);

  useEffect(() => {
    if (!isOpen || !form.roleName) {
      setUsers([]);
      return;
    }
    const roleMatch = (roles || []).find(
      (r) => (r.name || r.code) === form.roleName,
    );
    const roleId = roleMatch?.id;
    let cancelled = false;
    setLoadingUsers(true);
    getUsers({
      page: 1,
      limit: 200,
      status: "Active",
      ...(roleId ? { roleId } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        const rows =
          res?.result?.users ||
          res?.result?.data ||
          res?.data ||
          [];
        const list = Array.isArray(rows) ? rows : [];
        // Client-side role filter if API ignored roleId
        setUsers(
          roleId
            ? list.filter(
                (u) =>
                  u.roleId === roleId ||
                  u.role?.id === roleId ||
                  u.role?.name === form.roleName,
              )
            : list,
        );
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, form.roleName, roles]);

  const roleOptions = useMemo(
    () =>
      (roles || [])
        .map((r) => r.name || r.code)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [roles],
  );

  const setField = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "roleName") next.userId = "";
      return next;
    });
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!form.visitNote) nextErrors.visitNote = "Visit note is required";
    if (!form.roleName) nextErrors.roleName = "Role is required";
    if (!form.userId) nextErrors.userId = "User is required";
    const rateNum = Number(form.rate);
    if (form.rate === "" || Number.isNaN(rateNum) || rateNum < 0) {
      nextErrors.rate = "Enter a valid rate (0 or greater)";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave?.({
        visitNote: form.visitNote,
        roleName: form.roleName,
        userId: Number(form.userId),
        rate: rateNum,
        code: form.code.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Staff Pay Rate" : "Create Staff Pay Rate"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}

      <LibraryField
        id="pay-visit"
        label="Visit Note"
        required
        error={errors.visitNote}
        hint="Same visit types used in patient Eval & Orders / schedule"
      >
        <div className="relative">
          <select
            id="pay-visit"
            className={librarySelectClass(errors.visitNote)}
            value={form.visitNote}
            onChange={(e) => setField("visitNote", e.target.value)}
          >
            {STAFF_PAY_VISIT_NOTES.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="pay-role" label="Role" required error={errors.roleName}>
          <div className="relative">
            <select
              id="pay-role"
              className={librarySelectClass(errors.roleName)}
              value={form.roleName}
              onChange={(e) => setField("roleName", e.target.value)}
            >
              <option value="">Select role</option>
              {roleOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>

        <LibraryField id="pay-user" label="User" required error={errors.userId}>
          <div className="relative">
            <select
              id="pay-user"
              className={librarySelectClass(errors.userId)}
              value={form.userId}
              disabled={!form.roleName || loadingUsers}
              onChange={(e) => setField("userId", e.target.value)}
            >
              <option value="">
                {!form.roleName
                  ? "Select role first"
                  : loadingUsers
                    ? "Loading users…"
                    : "Select user"}
              </option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {[u.firstName, u.lastName].filter(Boolean).join(" ") ||
                    u.email ||
                    `User #${u.id}`}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="pay-rate" label="Rate ($)" required error={errors.rate}>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500 pointer-events-none">
              $
            </span>
            <input
              id="pay-rate"
              type="number"
              min="0"
              step="0.01"
              className={`${libraryInputClass(errors.rate)} pl-7`}
              value={form.rate}
              onChange={(e) => setField("rate", e.target.value)}
              placeholder="0.00"
            />
          </div>
        </LibraryField>
        <LibraryField id="pay-code" label="Code">
          <input
            id="pay-code"
            className={libraryInputClass()}
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            maxLength={50}
          />
        </LibraryField>
      </div>

      <LibraryField id="pay-status" label="Status">
        <div className="relative">
          <select
            id="pay-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default StaffPayRateFormModal;
